import { withDevtools } from '@ngrx-toolkit/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, exhaustMap, filter, of, pipe, tap } from 'rxjs';
import { computed, inject } from '@angular/core';
import { DataScope, IQDataProcessingFeatures } from '@maximizer/ai/data-access';
import { User } from '@maximizer/core/shared/domain';
import { UserService } from '@maximizer/core/shared/data-access';
import { MailboxType, OutlookPreferences, Vertical } from '../models';
import { UserSyncConfiguration } from '@maximizer/outlook-sync/data-access';
import {
  AbEntryLastContactDatePreference,
  EntrySearchGroupType,
} from '@maximizer/outlook/shared/domain';

type OutlookState = {
  users: User[];
  usersLoading: boolean;
  inMaximizerIds: string[];
  relatedSearchPhrase: string | null;
  entrySearchPhrase: string | null;
  entrySearchDisplayCounts: Partial<Record<EntrySearchGroupType, number>>;
  versionValidForNewSync: boolean;
  hasActiveAccount: boolean;
  graphMailAccount: string;
  doesGraphAccountMatchInbox: boolean;
  hideMismatchWarning: boolean;
  syncConfiguration: UserSyncConfiguration | undefined;
  hasInvalidComposeProperties: boolean;
  isCloud: boolean | undefined;
  isSharedMailbox: boolean;
  mailboxType: MailboxType;
  isComposeIntervalDefined: boolean;
  preferences: OutlookPreferences;
  preferencesConfigurationId: string | null;
  preferencesConfigurationUserId: string | null;
  isAuthorized: boolean;
  lastContactDatePreferences: AbEntryLastContactDatePreference[];
  isSentFolder: boolean;
  vertical: Vertical;
  language: string;
  timeFormatLong: string;
  timeFormatShort: string;
  hasSyncErrors: boolean;
  syncHealthLastChecked: number | null;
  syncHealthCheckRequested: boolean;
  adminDataProcessingFeatures: IQDataProcessingFeatures | null;
  adminDataScope: DataScope | null;
  userAnonymizationEnabled: boolean;
  userGlobalServersEnabled: boolean;
  userWebDataEnabled: boolean;
  saveAppointmentShown: boolean;
};

const initialState: OutlookState = {
  users: [],
  usersLoading: false,
  inMaximizerIds: [],
  relatedSearchPhrase: null,
  entrySearchPhrase: null,
  entrySearchDisplayCounts: {},
  versionValidForNewSync: false,
  hasActiveAccount: false,
  graphMailAccount: '',
  doesGraphAccountMatchInbox: true,
  hideMismatchWarning: false,
  syncConfiguration: undefined,
  hasInvalidComposeProperties: false,
  isCloud: undefined,
  isSharedMailbox: false,
  mailboxType: MailboxType.Personal,
  isComposeIntervalDefined: false,
  preferences: {
    saveEmailDetail: true,
    saveNow: false,
    saveAppointmentDetail: false,
  },
  preferencesConfigurationId: null,
  preferencesConfigurationUserId: null,
  isAuthorized: false,
  lastContactDatePreferences: [],
  isSentFolder: false,
  vertical: Vertical.Base,
  language: 'en',
  timeFormatLong: 'h:mm:ss a z',
  timeFormatShort: 'h:mm a',
  hasSyncErrors: false,
  syncHealthLastChecked: null,
  syncHealthCheckRequested: false,
  adminDataProcessingFeatures: null,
  adminDataScope: null,
  userAnonymizationEnabled: false,
  userGlobalServersEnabled: false,
  userWebDataEnabled: false,
  saveAppointmentShown: false,
};

export const OutlookStore = signalStore(
  withDevtools('outlook'),
  withState({
    ...initialState,
    inMaximizerIds: JSON.parse(
      sessionStorage.getItem('inMaximizerIds') ?? '[]',
    ),
    syncHealthLastChecked: JSON.parse(
      sessionStorage.getItem('syncHealthLastChecked') ?? 'null',
    ) as number | null,
  }),
  withComputed((store) => ({
    appointmentSyncEnabled: computed(() => {
      const syncConfiguration = store.syncConfiguration();
      return (
        !!syncConfiguration?.enabled &&
        syncConfiguration.appointmentsSync?.enabled
      );
    }),
    fullDateTimeFormat: computed(
      () => `dd MMMM yyyy ${store.timeFormatShort() ?? 'h:mm a'}`,
    ),
    userEmails: computed(() => {
      const emails = new Set<string>();
      for (const user of store.users()) {
        for (const email of [user.email1, user.email2, user.email3]) {
          if (email?.email) {
            emails.add(email.email.toLowerCase());
          }
        }
      }
      return emails;
    }),
  })),
  withMethods((store, userService = inject(UserService)) => ({
    loadUsers: rxMethod<void>(
      pipe(
        filter(() => !store.usersLoading() && store.users().length === 0),
        tap(() => patchState(store, { usersLoading: true })),
        exhaustMap(() =>
          userService.getUsers().pipe(
            tap((users) => patchState(store, { users, usersLoading: false })),
            catchError(() => {
              patchState(store, { usersLoading: false });
              return of([]);
            }),
          ),
        ),
      ),
    ),
    updateInMaximizerIdList(inMaximizerIds: string[]) {
      patchState(store, () => ({ inMaximizerIds }));
      sessionStorage.setItem('inMaximizerIds', JSON.stringify(inMaximizerIds));
    },
    setRelatedSearchContext(term: string) {
      patchState(store, () => ({ relatedSearchPhrase: term }));
      sessionStorage.setItem('relatedSearchPhrase', term);
    },
    clearRelatedSearchContext() {
      patchState(store, () => ({ relatedSearchPhrase: null }));
      sessionStorage.removeItem('relatedSearchPhrase');
    },
    setEntrySearchContext(term: string) {
      patchState(store, () => ({ entrySearchPhrase: term }));
    },
    clearEntrySearchContext() {
      patchState(store, () => ({
        entrySearchPhrase: null,
        entrySearchDisplayCounts: {},
      }));
    },
    setEntrySearchDisplayCounts(
      counts: Partial<Record<EntrySearchGroupType, number>>,
    ) {
      patchState(store, () => ({ entrySearchDisplayCounts: counts }));
    },
    updateVersion(valid: boolean) {
      patchState(store, () => ({ versionValidForNewSync: valid }));
    },
    setHasActiveAccount(active: boolean) {
      patchState(store, () => ({ hasActiveAccount: active }));
    },
    setHasInvalidComposeProperties(valid: boolean) {
      patchState(store, () => ({ hasInvalidComposeProperties: valid }));
    },
    setDoesActiveAccountMatch(match: boolean) {
      patchState(store, () => ({ doesGraphAccountMatchInbox: match }));
    },
    setHideMismatchWarning(hide: boolean) {
      patchState(store, () => ({ hideMismatchWarning: hide }));
    },
    setGraphMailAccount(name: string) {
      patchState(store, () => ({ graphMailAccount: name }));
    },
    updateSyncConfiguration(
      syncConfiguration: UserSyncConfiguration | undefined,
    ): void {
      patchState(store, () => ({ syncConfiguration }));
    },
    setIsCloud(isCloud: boolean) {
      patchState(store, () => ({ isCloud }));
    },
    setIsSharedMailbox(isSharedMailbox: boolean) {
      patchState(store, () => ({ isSharedMailbox }));
    },
    setIsComposeIntervalDefined(isComposeIntervalDefined: boolean) {
      patchState(store, () => ({ isComposeIntervalDefined }));
    },
    setPreferences(preferences: OutlookPreferences) {
      patchState(store, () => ({ preferences }));
    },
    storePreferencesConfigurationId(
      preferencesConfigurationId: string,
      preferencesConfigurationUserId: string,
    ) {
      patchState(store, () => ({
        preferencesConfigurationId,
        preferencesConfigurationUserId,
      }));
    },
    setMailboxType(mailboxType: MailboxType) {
      patchState(store, () => ({ mailboxType }));
    },
    setIsAuthorized(isAuthorized: boolean) {
      patchState(store, () => ({ isAuthorized }));
    },
    setLastContactDatePreferences(
      preferences: AbEntryLastContactDatePreference[],
    ) {
      patchState(store, () => ({ lastContactDatePreferences: preferences }));
      localStorage.setItem(
        'lastContactDatePreferences',
        JSON.stringify(preferences),
      );
    },
    setIsSentFolder(isSentFolder: boolean) {
      patchState(store, () => ({ isSentFolder }));
    },
    updateVertical(vertical: Vertical) {
      patchState(store, () => ({ vertical }));
    },
    setLanguage(language: string) {
      patchState(store, () => ({ language }));
    },
    setTimeFormats(timeFormatShort: string, timeFormatLong: string) {
      patchState(store, () => ({ timeFormatShort, timeFormatLong }));
    },
    setHasSyncErrors(hasSyncErrors: boolean) {
      patchState(store, () => ({ hasSyncErrors }));
    },
    setSyncHealthLastChecked(syncHealthLastChecked: number) {
      patchState(store, () => ({ syncHealthLastChecked }));
      sessionStorage.setItem(
        'syncHealthLastChecked',
        String(syncHealthLastChecked),
      );
    },
    clearSyncHealthLastChecked() {
      patchState(store, () => ({ syncHealthLastChecked: null }));
      sessionStorage.removeItem('syncHealthLastChecked');
    },
    requestSyncHealthCheck() {
      patchState(store, () => ({ syncHealthCheckRequested: true }));
    },
    clearSyncHealthCheckRequest() {
      patchState(store, () => ({ syncHealthCheckRequested: false }));
    },
    setAdminDataProcessingFeatures(features: IQDataProcessingFeatures) {
      patchState(store, () => ({ adminDataProcessingFeatures: features }));
    },
    setAdminDataScope(adminDataScope: DataScope) {
      patchState(store, () => ({ adminDataScope }));
    },
    setUserAnonymizationEnabled(userAnonymizationEnabled: boolean) {
      patchState(store, () => ({ userAnonymizationEnabled }));
    },
    setUserGlobalServersEnabled(userGlobalServersEnabled: boolean) {
      patchState(store, () => ({ userGlobalServersEnabled }));
    },
    setUserWebDataEnabled(userWebDataEnabled: boolean) {
      patchState(store, () => ({ userWebDataEnabled }));
    },
    setSaveAppointmentShown(saveAppointmentShown: boolean) {
      patchState(store, () => ({ saveAppointmentShown }));
    },
  })),
);

export type OutlookStore = InstanceType<typeof OutlookStore>;
